const RECENT_APPROVERS_LIMIT = 5;

/**
 * Build a storage key scoped to the current client and user so each requester
 * keeps an independent recent-approver list inside the browser.
 */
export function getRecentApproversStorageKey({ clientId, username }) {
  return `recent_approvers:${clientId}:${username}`;
}

/**
 * Read the recent approver ids from localStorage. Invalid or missing data
 * returns an empty list so the picker can safely fall back to backend order.
 */
export function getRecentApproverIds({ clientId, username }) {
  if (typeof window === 'undefined' || !window.localStorage || !clientId || !username) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(getRecentApproversStorageKey({ clientId, username }));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => id !== null && id !== undefined) : [];
  } catch (_error) {
    return [];
  }
}

/**
 * Persist a bounded recent-approver list. We only store approver ids, not the
 * full user records, to keep the cache small and resilient to user-data drift.
 */
export function setRecentApproverIds({ clientId, username, approverIds }) {
  if (typeof window === 'undefined' || !window.localStorage || !clientId || !username) {
    return;
  }

  const normalized = [];
  const seen = new Set();

  approverIds.forEach((id) => {
    if (id === null || id === undefined || seen.has(id)) {
      return;
    }
    seen.add(id);
    normalized.push(id);
  });

  window.localStorage.setItem(
    getRecentApproversStorageKey({ clientId, username }),
    JSON.stringify(normalized.slice(0, RECENT_APPROVERS_LIMIT))
  );
}

function areIdsEqual(leftIds = [], rightIds = []) {
  if (leftIds.length !== rightIds.length) {
    return false;
  }

  return leftIds.every((id, index) => id === rightIds[index]);
}

/**
 * Update the cache from a modal interaction.
 * - Added approvers become the most recent entries.
 * - Pure reorders refresh the selected list order as the recent order.
 * - Pure removals do not change recency because nothing new was picked.
 */
export function updateRecentApproverIds({
  clientId,
  username,
  previousApproverIds = [],
  nextApproverIds = [],
}) {
  if (areIdsEqual(previousApproverIds, nextApproverIds)) {
    return;
  }

  const currentRecentIds = getRecentApproverIds({ clientId, username });
  const previousIdsSet = new Set(previousApproverIds);
  const addedIds = nextApproverIds.filter((id) => !previousIdsSet.has(id));

  if (addedIds.length > 0) {
    const updatedRecentIds = [...currentRecentIds];

    addedIds.forEach((id) => {
      const existingIndex = updatedRecentIds.indexOf(id);
      if (existingIndex !== -1) {
        updatedRecentIds.splice(existingIndex, 1);
      }
      updatedRecentIds.unshift(id);
    });

    setRecentApproverIds({ clientId, username, approverIds: updatedRecentIds });
    return;
  }

  const updatedRecentIds = [...currentRecentIds];

  [...nextApproverIds].reverse().forEach((id) => {
    const existingIndex = updatedRecentIds.indexOf(id);
    if (existingIndex !== -1) {
      updatedRecentIds.splice(existingIndex, 1);
    }
    updatedRecentIds.unshift(id);
  });

  setRecentApproverIds({ clientId, username, approverIds: updatedRecentIds });
}

/**
 * Keep the backend-provided order for the non-recent remainder while bubbling
 * known recent approvers to the top in most-recent-first order.
 */
export function sortApproversByRecentIds(users = [], recentApproverIds = []) {
  if (!Array.isArray(users) || recentApproverIds.length === 0) {
    return users;
  }

  const rankById = new Map(recentApproverIds.map((id, index) => [id, index]));

  return [...users].sort((leftUser, rightUser) => {
    const leftRank = rankById.get(leftUser.id_user);
    const rightRank = rankById.get(rightUser.id_user);

    if (leftRank === undefined && rightRank === undefined) {
      return 0;
    }
    if (leftRank === undefined) {
      return 1;
    }
    if (rightRank === undefined) {
      return -1;
    }

    return leftRank - rightRank;
  });
}
